import { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import {
  COMPANY_EMAIL,
  COMPANY_EMAIL_HREF,
  COMPANY_PHONE_DISPLAY,
  COMPANY_PHONE_TEL,
} from '../constants/companyContact';
import { useFloatingCorner } from '../contexts/FloatingCornerContext';

const OPPORTUNITY_MODAL_DELAY_MS = 5000;

type Props = { reduceMotion?: boolean };

/**
 * Centered “Your opportunity” modal — only on `/getstarted`. Opens 5 seconds after load.
 * After close, compact Get Access + Schedule a call actions sit above the corner stack. Dismiss state is
 * in-memory only; leaving the page resets; a full reload on Get Started runs the delay again.
 */
export function OpportunityCallout({ reduceMotion }: Props) {
  const location = useLocation();
  const { eventAnnouncementExpanded, supportMessengerOpen } = useFloatingCorner();
  const onAdmin = location.pathname.startsWith('/admin');
  const onGetStarted =
    location.pathname === '/getstarted' || location.pathname === '/getstarted/';
  const titleId = useId();
  const [mounted, setMounted] = useState(false);
  /** Opens after delay once mounted on Get Started (non-admin). */
  const [sheetOpen, setSheetOpen] = useState(false);
  const [fabPair, setFabPair] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!onGetStarted) {
      setSheetOpen(false);
      setFabPair(false);
    }
  }, [onGetStarted]);

  useEffect(() => {
    if (!mounted || onAdmin || !onGetStarted) return;
    const t = window.setTimeout(() => setSheetOpen(true), OPPORTUNITY_MODAL_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [mounted, onAdmin, onGetStarted]);

  const dismiss = useCallback(() => {
    setSheetOpen(false);
    setFabPair(true);
  }, []);

  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetOpen, dismiss]);

  if (!mounted || onAdmin || !onGetStarted) {
    return null;
  }

  const dialogMotion = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.94 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.94 },
        transition: { type: 'spring' as const, damping: 26, stiffness: 320 },
      };

  const fabMotion = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 12 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] as const },
      };

  /**
   * Marketing corner stack grows upward: support panel + event block stack vertically.
   * Lift is additive when both are open (Math.max was too small and pills overlapped the webinar card).
   */
  const SUPPORT_OPEN_LIFT_REM = 14;
  const EVENT_OPEN_LIFT_REM = 24;
  const cornerLiftRem =
    (supportMessengerOpen ? SUPPORT_OPEN_LIFT_REM : 0) +
    (eventAnnouncementExpanded ? EVENT_OPEN_LIFT_REM : 0);

  const fabStackBottom = `calc(${12.75 + cornerLiftRem}rem + env(safe-area-inset-bottom, 0px))`;

  const ui = (
    <>
      <AnimatePresence>
        {sheetOpen ? (
          <motion.div
            key="bcc-opportunity-modal"
            className="fixed inset-0 z-[399] flex items-center justify-center px-3 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] pointer-events-none"
            initial={reduceMotion ? undefined : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <button
              type="button"
              aria-label="Close opportunity panel"
              className="absolute inset-0 z-0 bg-[var(--home-zone-void)]/65 backdrop-blur-[2px] pointer-events-auto"
              onClick={dismiss}
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              className="pointer-events-auto relative z-10 w-full max-w-[min(100vw-1.5rem,22.5rem)] overflow-hidden rounded-2xl border border-white/15 bg-[var(--home-zone-dark)]/95 px-4 py-4 shadow-[0_28px_80px_-24px_rgb(0_0_0/0.55)] ring-1 ring-white/[0.06] backdrop-blur-lg sm:max-w-md sm:px-5 sm:py-5"
              {...dialogMotion}
            >
                <div className="flex flex-col items-center text-center">
                  <h2
                    id={titleId}
                    className="font-display text-lg font-bold uppercase tracking-[0.08em] text-[var(--home-text-on-dark)] sm:text-xl"
                  >
                    Your opportunity
                  </h2>

                  <div className="mt-3 w-full rounded-xl border border-[#e8c468]/45 bg-white/[0.04] px-3 py-3.5 ring-1 ring-white/[0.06] sm:mt-4 sm:px-4 sm:py-4">
                    <p className="font-display text-[0.62rem] font-bold uppercase tracking-[0.16em] text-[var(--home-text-on-dark)]">
                      Minimum investments
                    </p>
                    <div className="mt-2.5 flex w-full flex-col gap-3 sm:gap-3.5">
                      <div>
                        <p className="font-display text-[0.58rem] font-bold uppercase tracking-[0.14em] text-[var(--home-miami-blue)]">
                          Class A
                        </p>
                        <p className="mt-0.5 font-display text-2xl font-extrabold tabular-nums tracking-tight text-[var(--home-miami-blue)] sm:text-3xl">
                          $250,000
                        </p>
                      </div>
                      <div>
                        <p className="font-display text-[0.58rem] font-bold uppercase tracking-[0.14em] text-[var(--home-miami-blue)]">
                          Class B
                        </p>
                        <p className="mt-0.5 font-display text-2xl font-extrabold tabular-nums tracking-tight text-[var(--home-miami-blue)] sm:text-3xl">
                          $50,000
                        </p>
                      </div>
                    </div>
                    <div className="mt-2.5 space-y-1 font-body text-[0.78rem] leading-snug text-[var(--home-muted-on-dark)] sm:text-[0.82rem]">
                      <p>If you&apos;re sitting on cash…</p>
                      <p>If your retirement isn&apos;t working hard enough…</p>
                      <p>If you want real income backed by real assets…</p>
                    </div>
                    <p className="mt-2.5 font-display text-[0.7rem] font-bold uppercase tracking-[0.08em] text-[var(--home-text-on-dark)] sm:text-xs">
                      This is your opportunity.
                    </p>
                    <a
                      href={COMPANY_PHONE_TEL}
                      className="mt-3 inline-flex w-full items-center justify-center rounded-xl bg-[#e8c468] px-4 py-2.5 font-display text-[0.6rem] font-bold uppercase tracking-[0.14em] text-[var(--home-zone-void)] shadow-lg shadow-black/25 ring-1 ring-black/10 transition hover:brightness-105 sm:py-3 sm:text-[0.62rem]"
                    >
                      Schedule a call
                    </a>
                    <p className="mt-2.5 font-body text-[0.72rem] text-[var(--home-muted-on-dark)] sm:text-[0.78rem]">
                      Questions about this opportunity?
                    </p>
                    <p className="mt-0.5 font-body text-[0.78rem] font-semibold text-[var(--home-text-on-dark)] sm:text-[0.82rem]">
                      <a
                        href={COMPANY_EMAIL_HREF}
                        className="break-all underline decoration-[var(--home-miami-blue)]/50 underline-offset-2 hover:text-[var(--home-miami-blue)]"
                      >
                        {COMPANY_EMAIL}
                      </a>
                    </p>
                    <p className="mt-0.5 font-display text-[0.78rem] font-bold tabular-nums text-[var(--home-text-on-dark)] sm:text-[0.85rem]">
                      <a href={COMPANY_PHONE_TEL} className="hover:text-[var(--home-miami-blue)]">
                        {COMPANY_PHONE_DISPLAY}
                      </a>
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="absolute right-1.5 top-1.5 rounded-lg p-1.5 text-[var(--home-muted-on-dark)] transition hover:bg-white/10 hover:text-[var(--home-text-on-dark)] sm:right-2 sm:top-2"
                  onClick={dismiss}
                  aria-label="Close"
                >
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                  </svg>
                </button>
              </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {fabPair && !sheetOpen ? (
          <motion.div
            className="fixed z-[306] flex flex-col items-end gap-2 pointer-events-none"
            style={{
              bottom: fabStackBottom,
              right: 'max(1rem, env(safe-area-inset-right, 0px))',
            }}
            {...fabMotion}
          >
            <Link
              to="/register"
              className="pointer-events-auto rounded-full bg-[var(--home-miami-blue)] px-4 py-2.5 font-display text-[0.58rem] font-bold uppercase tracking-[0.12em] text-[var(--home-zone-void)] shadow-[0_10px_36px_-8px_rgb(0_0_0/0.5)] ring-2 ring-white/30 transition hover:brightness-105"
            >
              Get Access
            </Link>
            <a
              href={COMPANY_PHONE_TEL}
              className="pointer-events-auto rounded-full border-2 border-[#e8c468]/80 bg-[var(--home-zone-dark)]/90 px-4 py-2.5 font-display text-[0.58rem] font-bold uppercase tracking-[0.12em] text-[#fde68a] shadow-[0_10px_36px_-8px_rgb(0_0_0/0.45)] backdrop-blur-md transition hover:border-[#e8c468] hover:bg-[var(--home-zone-dark)]"
            >
              Schedule a call
            </a>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );

  return createPortal(ui, document.body);
}
